// What a page does to keep a feed's trains live, whatever the feed: poll it
// while shown, fetch its static files with a deadline, and keep its trains
// under its own keys in the page's one store. A GTFS city (client.ts, on its
// own page and the world map's) and London on the world map both use these.

import * as Core from '@tol/core/core';
import * as Feed from '@tol/core/feed';
import type { ComputedTrains, LineId, TrainStore } from '@tol/core/types';
import { LOADING_POLL_MS } from './trains.ts';

const RETRY_MS = 3000;

export function createPollHintDelay(now: () => number): (hint?: unknown) => number | null {
  let previous: number | undefined;
  let overdue = 0;
  return (hint) => {
    if (typeof hint !== 'number' || !Number.isFinite(hint)) {
      previous = undefined;
      overdue = 0;
      return null;
    }
    if (hint !== previous) overdue = 0;
    previous = hint;
    const remaining = hint - now();
    if (remaining > 0) return Math.max(1000, Math.min(120_000, remaining));
    const delay = Math.min(2000 * 2 ** overdue, 8000);
    overdue = Math.min(overdue + 1, 2);
    return delay;
  };
}

export interface PollLoop {
  // Polls now; the promise settles with that first poll. Idempotent.
  readonly start: () => Promise<void>;
  // Ask again now. An in-flight poll finishes first; repeated refreshes
  // coalesce into one follow-up.
  readonly refresh: () => void;
  // Abort the current request and immediately poll again in a new scope.
  readonly replace: () => void;
  readonly stop: () => void;
  readonly running: () => boolean;
  // Running, and no answer with everything in yet.
  readonly loading: () => boolean;
}

// A poll loop: poll(signal) now and every pollMs after it settles, sooner
// (3, 6, 12 s...) after a failure, and LOADING_POLL_MS after an answer that
// says more is on its way (poll resolves true: a server's first refresh
// after a quiet spell), until stop(). onLoaded: when loading stops or
// restarts (a later partial answer reenters loading).
export function pollLoop(
  poll: (signal: AbortSignal) => Promise<boolean>,
  pollMs: number,
  onError: (error: unknown) => void,
  onLoaded?: () => void,
  nextDelay?: () => number | null,
): PollLoop {
  let active: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  let loading = false;
  let polling: AbortController | null = null;
  let refresh = false;
  async function run(batch: AbortController): Promise<void> {
    polling = batch;
    try {
      const more = await poll(batch.signal);
      if (active === batch && loading !== more) {
        loading = more;
        onLoaded?.();
      }
      failures = 0;
    } catch (error) {
      if (batch.signal.aborted) return;
      failures++;
      onError(error);
    } finally {
      if (polling === batch) polling = null;
    }
    if (active !== batch) return;
    if (refresh) {
      refresh = false;
      return run(batch);
    }
    const delay =
      failures > 0
        ? Math.min(RETRY_MS * 2 ** (failures - 1), pollMs)
        : (nextDelay?.() ?? (loading ? LOADING_POLL_MS : pollMs));
    timer = setTimeout(() => void run(batch), delay);
  }
  return {
    start() {
      if (active) return Promise.resolve();
      active = new AbortController();
      failures = 0;
      loading = true;
      return run(active);
    },
    refresh() {
      if (!active) return;
      clearTimeout(timer);
      if (polling === active) refresh = true;
      else void run(active);
    },
    replace() {
      if (!active) return;
      clearTimeout(timer);
      active.abort();
      active = new AbortController();
      failures = 0;
      refresh = false;
      if (!loading) {
        loading = true;
        onLoaded?.();
      }
      void run(active);
    },
    stop() {
      clearTimeout(timer);
      active?.abort();
      active = null;
      refresh = false;
      loading = false;
    },
    running: () => active !== null,
    loading: () => loading,
  };
}

// Apply one response's trains for `lines` (the store's ids) under store key
// `key` (core's applyComputed), and clear the key's failure. false if it was
// an older snapshot than the one applied (a lagging cache).
export function applyRegion(
  store: TrainStore,
  key: number,
  lines: readonly LineId[],
  computed: ComputedTrains,
  dataTime: number,
  now: number,
): boolean {
  if (!Feed.applyComputed(store, Core.mergeTrains, key, lines, computed, dataTime, now, Date.now())) return false;
  store.failedGroups = store.failedGroups.filter((k) => k !== key);
  return true;
}

// Recompute the trains of the response applyRegion last accepted (new
// alerts, a fuller network): no request answered, so its receipt time and
// any failure since stay as they were.
export function recomputeRegion(
  store: TrainStore,
  key: number,
  lines: readonly LineId[],
  computed: ComputedTrains,
  dataTime: number,
  now: number,
): boolean {
  const received = store.groupUpdates.get(key) ?? Date.now();
  return Feed.applyComputed(store, Core.mergeTrains, key, lines, computed, dataTime, now, received);
}

// Forget a region: its trains, arrivals and store keys.
export function clearRegion(store: TrainStore, lines: readonly LineId[], keys: readonly number[]): void {
  const own = new Set(lines);
  for (const [id, t] of store.trains) if (own.has(t.pred.lineId)) store.trains.delete(id);
  for (const [id, arrivals] of store.byStation) {
    const keep = arrivals.filter((a) => !own.has(a.lineId));
    if (keep.length > 0) store.byStation.set(id, keep);
    else store.byStation.delete(id);
  }
  for (const k of keys) {
    store.groupTimes.delete(k);
    store.groupUpdates.delete(k);
  }
  store.failedGroups = store.failedGroups.filter((k) => !keys.includes(k));
}

const STATIC_TIMEOUT_MS = 15_000;

// A static file (a network, its geometry), its HTTP status on the error it
// throws (a city page reloads on 409, another deploy's server). A stalled
// static host cannot leave a load pending forever.
// Requests a page started before it had the code that asks for them (the
// world map's place, fetched as the page starts: apps/world app.ts), by URL.
// loadJSON takes each once; a failed one is asked for again.
const primed = new Map<string, Promise<unknown>>();
export function primeJSON(url: string): void {
  if (primed.has(url)) return;
  const got = fetchJSON(url);
  // Unhandled until loadJSON takes it, which may be never.
  got.catch(() => null);
  primed.set(url, got);
}

type AbortView = Readonly<Pick<AbortSignal, 'addEventListener' | 'reason' | 'removeEventListener' | 'throwIfAborted'>>;

function abortReason(signal: AbortView): Error {
  return signal.reason instanceof Error ? signal.reason : new DOMException('The operation was aborted', 'AbortError');
}

function withSignal(value: Readonly<Promise<unknown>>, signal: AbortView): Promise<unknown> {
  signal.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = (): void => {
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', abort, { once: true });
    void value.then(
      (result) => {
        signal.removeEventListener('abort', abort);
        resolve(result);
        return null;
      },
      (error: unknown) => {
        signal.removeEventListener('abort', abort);
        reject(error instanceof Error ? error : new Error(String(error)));
        return null;
      },
    );
  });
}

export async function loadJSON(url: string, signal?: AbortSignal): Promise<unknown> {
  const early = primed.get(url);
  if (early) {
    primed.delete(url);
    try {
      return await (signal ? withSignal(early, signal) : early);
    } catch {
      signal?.throwIfAborted();
      // A failed primed request is asked for again, below.
    }
  }
  return fetchJSON(url, signal);
}

async function fetchJSON(url: string, signal?: AbortSignal): Promise<unknown> {
  signal?.throwIfAborted();
  const controller = new AbortController();
  const abort = (): void => {
    controller.abort(signal?.reason);
  };
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(() => {
    controller.abort(new Error(`${url}: no answer in ${String(STATIC_TIMEOUT_MS)} ms`));
  }, STATIC_TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: controller.signal });
    if (!r.ok) throw Object.assign(new Error(`${url}: HTTP ${String(r.status)}`), { status: r.status });
    return await r.json();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

const GEOMETRY_RETRY_MS = 3000;
const GEOMETRY_RETRY_MAX_MS = 60_000;

export interface OptionalJSON<T> {
  readonly first: Promise<T | null>;
  readonly cancel: () => void;
}

interface OptionalJSONErrors {
  readonly stopsRetry: (error: unknown) => boolean;
  readonly stoppedRetry: (error: unknown) => void;
}

// Optional geometry may fail while its network remains useful. Keep the
// straight-line network, retry with capped backoff, and publish the repaired
// one when the static host recovers. A generation-bound caller can stop a
// stale URL: its first failure rejects the load, and a later one reports that
// the installed selection needs replacing.
export function loadOptionalJSON<T>(
  url: string,
  accept: (value: unknown) => value is T,
  recovered: (value: T) => void,
  errors?: OptionalJSONErrors,
  signal?: AbortSignal,
): OptionalJSON<T> {
  let active = true;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let requestController: AbortController | null = null;
  const stop = (): void => {
    active = false;
    clearTimeout(timer);
    requestController?.abort(signal?.reason);
    signal?.removeEventListener('abort', stop);
  };
  signal?.addEventListener('abort', stop, { once: true });
  if (signal?.aborted === true) stop();

  async function request(isRetry: boolean): Promise<T | null> {
    if (!active) return null;
    requestController = new AbortController();
    try {
      const value = await loadJSON(url, requestController.signal);
      if (!accept(value)) throw new TypeError(`${url}: invalid data`);
      active = false;
      signal?.removeEventListener('abort', stop);
      if (isRetry) recovered(value);
      return value;
    } catch (error) {
      if (!active) return null;
      if (errors?.stopsRetry(error) === true) {
        active = false;
        if (isRetry) {
          errors.stoppedRetry(error);
          return null;
        }
        throw error;
      }
      failures++;
      const delay = Math.min(GEOMETRY_RETRY_MS * 2 ** (failures - 1), GEOMETRY_RETRY_MAX_MS);
      timer = setTimeout(() => void request(true), delay);
      if (typeof timer === 'object' && timer !== null) {
        const unref: unknown = Reflect.get(timer, 'unref');
        if (typeof unref === 'function') Reflect.apply(unref, timer, []);
      }
      return null;
    } finally {
      requestController = null;
    }
  }

  return {
    first: request(false),
    cancel: stop,
  };
}
